import { useStore } from '@/store/useStore';
import { usePerms } from '@/auth/usePerms';
import { useMemo, useState, useEffect, Fragment } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, Building2, Gamepad2, Users, Banknote, Settings as SettingsIcon, Car, Gamepad, Calendar as CalendarIcon, UserX, X } from 'lucide-react';
import { addDays, inBranchTz, startOfDay } from '@/utils/time';
import { cn } from '@/utils/cn';
import { useTzShiftMs } from '@/hooks/useTzShift';
import { DEMO_CURRENT_USER_NAME, DEAL_TO_FUNNEL, FUNNEL_BY_KEY, effectiveAmount, CURRENCY_LABEL, type Branch, type Currency, type Deal, type FunnelStatus, type ID } from '@/types';
import { makeRateLookup, toKzt } from '@/utils/currency';

const DOW = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

const CUR = (c: 'KZT' | 'UZS' | 'AZN' | 'KGS') => (c === 'KZT' ? '₸' : c === 'UZS' ? 'сум' : c === 'KGS' ? 'сом' : '₼');

function hhmm(ts?: number): string {
  if (ts == null) return '';
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
function ddmmyy(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getFullYear()).slice(2)}`;
}
function startOfWeek(ts: number): number {
  const d = new Date(ts); d.setHours(0, 0, 0, 0);
  const dow = (d.getDay() + 6) % 7; // Пн = 0
  d.setDate(d.getDate() - dow);
  return d.getTime();
}
function initials(name: string): string {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase();
}
function fmtSpaced(v: number, cur: string): string {
  return `${Math.round(v).toLocaleString('ru-RU')} ${cur}`;
}
function fmtShort(v: number, cur: string): string {
  if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}В${cur}`;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace('.0', '')}М${cur}`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)}К${cur}`;
  return `${v}${cur}`;
}
// Названия месяцев в именительном (для шапки «ИЮНЬ 2026»).
const MONTHS_NOM = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
function startOfMonth(ts: number): number { const d = new Date(ts); d.setDate(1); d.setHours(0, 0, 0, 0); return d.getTime(); }
function addMonths(ts: number, n: number): number { const d = new Date(ts); d.setMonth(d.getMonth() + n); return d.getTime(); }

type Grouping = 'week' | 'month' | 'custom';
type DateType = 'event' | 'sale';
// Тип группировки → шаг навигации [from, to) и подпись периода.
function periodRange(g: Grouping, anchor: number, customFrom: number, customTo: number): { from: number; to: number } {
  if (g === 'month') { const f = startOfMonth(anchor); return { from: f, to: addMonths(f, 1) }; }
  if (g === 'custom') return { from: startOfDay(customFrom), to: startOfDay(customTo) + 24 * 3600_000 };
  return { from: anchor, to: anchor + 7 * 24 * 3600_000 }; // week
}
function periodLabel(g: Grouping, from: number, to: number): string {
  if (g === 'month') { const d = new Date(from); return `${MONTHS_NOM[d.getMonth()].toUpperCase()} ${d.getFullYear()}`; }
  return `${ddmmyy(from)} - ${ddmmyy(to - 24 * 3600_000)}`;
}

// Стадия воронки (из карточки) → бронь-статус (Предбронь / Бронь / Проведено).
// РАНЬШЕ считали по грубому DealStatus, и старые сделки из Битрикса приходили
// со status='new' → попадали в «предбронь», хотя в карточке у них стадия «Бронь»
// (и никакого таймера предброни). Теперь считаем по воронке — по той стадии,
// что реально стоит в карточке «Статус заказа», чтобы столбцы не врали.
type Booking = 'prebron' | 'bron' | 'provedeno';
function bookingOfFunnel(fs: FunnelStatus | null | undefined): Booking | null {
  switch (fs) {
    case 'prebooking': return 'prebron';                     // реальная предбронь (с таймером)
    case 'booking':
    case 'booking_barter':
    case 'reschedule': return 'bron';                        // бронь / бартер / перенос
    case 'done': return 'provedeno';                         // проведена
    default: return null;                                    // отмена / уточнение / нет — не в бронь-суммах
  }
}
const BOOKING: Record<Booking, { label: string; bg: string; fg: string }> = {
  prebron: { label: 'Предбронь', bg: '#FEF3C7', fg: '#B45309' },
  bron: { label: 'Бронь', bg: '#D1FAE5', fg: '#059669' },
  provedeno: { label: 'Проведено', bg: '#DBEAFE', fg: '#2563EB' },
};

const SEG_COLORS = ['#FAC443', '#F6757C', '#7F77DD', '#2DD4BF', '#6EA2F7', '#F59E0B', '#34D399', '#A78BFA', '#FB7185', '#60A5FA'];

// Стадии воронки как в эталоне (колонки при «показать все статусы»).
// Наши демо-статусы раскладываем по стадиям; стадий Перенос/уточнение/бартер
// в нашей модели пока нет — наполнятся из реального Битрикса (Этап 2).
// Колонки-статусы считаем по ВОРОНКЕ (funnel_status), а не по грубому DealStatus:
// Перенос/уточнение/бартер отличимы только на уровне воронки (в DealStatus они
// схлопываются в in_work/new). Ключ каждой колонки — её funnel_status.
const STAGE_COLS: { key: string; label: string; funnels: FunnelStatus[] }[] = [
  { key: 'prebron', label: 'Предбронь', funnels: ['prebooking'] },
  { key: 'bron', label: 'Бронь', funnels: ['booking'] },
  { key: 'otmena', label: 'Отмена', funnels: ['cancel'] },
  { key: 'provedena', label: 'Проведена', funnels: ['done'] },
  { key: 'perenos', label: 'Перенос', funnels: ['reschedule'] },
  { key: 'utochnenie', label: 'требует уточнения', funnels: ['clarify'] },
  { key: 'barter', label: 'Бронь (бартер)', funnels: ['booking_barter'] },
];

export function DashboardPage() {
  const { deals, branches, teams, employees, game_formats, settings, filterBranchId, setFilterBranch, setPage, setModal } = useStore();
  const currentUser = useStore((s) => s.currentUser);
  const canSwitchCity = usePerms().citySwitcher;
  // Управляющий/менеджер привязаны к своим городам — селектор не должен предлагать «Все
  // филиалы» и чужие города (данные и так ограничены на сервере, но и выбор прячем).
  const myCities = currentUser?.city_ids ?? [];
  // Города, отмеченные лично в карточке, сужают выбор ЛЮБОЙ роли (сервер и так отдаёт только
  // их данные — прячем и выбор, чтобы человек не открывал заведомо пустой филиал).
  const allowedBranches = myCities.length > 0
    ? branches.filter((b) => myCities.includes(b.id))
    : (canSwitchCity ? branches : []);
  // Ограниченный пользователь не должен «застрять» на чужом/«все» филиале:
  // — один город → жёстко его; — несколько → разрешаем «все мои», но не чужой филиал.
  useEffect(() => {
    if ((canSwitchCity && myCities.length === 0) || allowedBranches.length === 0) return;
    if (allowedBranches.length === 1) {
      if (filterBranchId !== allowedBranches[0].id) setFilterBranch(allowedBranches[0].id);
    } else {
      const ok = filterBranchId === 'all' || allowedBranches.some((b) => b.id === filterBranchId);
      if (!ok) setFilterBranch(allowedBranches[0].id);
    }
  }, [canSwitchCity, allowedBranches, filterBranchId, setFilterBranch]);
  const tzShiftMs = useTzShiftMs();
  const [day, setDay] = useState(() => startOfDay(Date.now()));
  const [onlyAssigned, setOnlyAssigned] = useState(false);
  const [gamesListOpen, setGamesListOpen] = useState(false);

  // ── Настройки блока «по менеджерам» (шестерёнка) ──
  const [grouping, setGrouping] = useState<Grouping>('week');
  const [dateType, setDateType] = useState<DateType>('event');
  const [showAllStatuses, setShowAllStatuses] = useState(false);
  const [gearOpen, setGearOpen] = useState(false);
  const [anchor, setAnchor] = useState(() => startOfWeek(Date.now()));      // начало периода (неделя/месяц)
  const [customFrom, setCustomFrom] = useState(() => startOfMonth(Date.now()));
  const [customTo, setCustomTo] = useState(() => startOfMonth(Date.now()) + 29 * 24 * 3600_000);
  // выбранные менеджеры (чекбоксы) + сортировка таблицы
  const [selectedMgrs, setSelectedMgrs] = useState<Set<string>>(new Set());
  const [sortDesc, setSortDesc] = useState(true);
  // пагинация таблицы
  const [perPage, setPerPage] = useState(10);
  const [tablePage, setTablePage] = useState(0);
  // Обобщённая модалка со списком сделок (плитка «Без менеджера» / клик по любой строке отчёта)
  const [listModal, setListModal] = useState<{ title: string; isNoMgr: boolean; deals: Deal[]; sum: number; navIds: ID[] } | null>(null);

  const period = periodRange(grouping, anchor, customFrom, customTo);
  // Сдвиг периода стрелками: неделя ±7д, месяц ±1мес, произвольно ±длина диапазона.
  const shiftPeriod = (dir: 1 | -1) => {
    if (grouping === 'month') { setAnchor((a) => addMonths(startOfMonth(a), dir)); return; }
    if (grouping === 'custom') {
      const span = startOfDay(customTo) - startOfDay(customFrom) + 24 * 3600_000;
      setCustomFrom((f) => f + dir * span); setCustomTo((t) => t + dir * span); return;
    }
    setAnchor((a) => a + dir * 7 * 24 * 3600_000);
  };

  // Дашборд приводит ВСЕ суммы к одной валюте по курсу Нацбанка на дату игры (см. kztOf
  // ниже) — складывать сумы с тенге напрямую нельзя, итоги врали в разы. В какой валюте
  // показывать — выбирает сам пользователь (по умолчанию тенге); выбор запоминается,
  // чтобы Ташкент мог всегда открывать дашборд в сумах.
  const [viewCurrency, setViewCurrency] = useState<Currency>(
    () => (localStorage.getItem('dash_currency') as Currency) || 'KZT',
  );
  useEffect(() => { localStorage.setItem('dash_currency', viewCurrency); }, [viewCurrency]);
  const cur = CUR(viewCurrency);
  const selectedBranch = filterBranchId !== 'all' ? branches.find((b) => b.id === filterBranchId) : null;
  const localOff = -new Date().getTimezoneOffset();

  // ── Ивент-филиал и город проведения ──
  // Ивент-филиал определяем ПО ДАННЫМ, а не по хардкоду id/названия: это дом-филиал(ы),
  // в которых лежат ивент-сделки (kind==='event'). На боевом портале id другой — поэтому
  // никаких «Астана/Алмата» в коде.
  // Ивент-филиал — это дом-филиал, ОТКУДА ивент зеркалится в город: у его сделок задан
  // «Город проведения» (mirror_branch_id). Именно наличие города, а не сам kind==='event',
  // отличает настоящий ивент-филиал от обычных корпоративных сделок (которые тоже бывают
  // kind==='event', но без города) — иначе выбор города вылезал бы на любом филиале.
  const eventBranchIds = useMemo(() => {
    const s = new Set<ID>();
    for (const d of deals) if (d.kind === 'event' && d.mirror_branch_id != null && d.branch_id != null) s.add(d.branch_id);
    return s;
  }, [deals]);
  const isEventBranchSelected = filterBranchId !== 'all' && eventBranchIds.has(filterBranchId as ID);

  // Города проведения ивента — динамический список: уникальные mirror_branch_id среди
  // ивент-сделок, сопоставленные со справочником филиалов, по алфавиту. Новый город
  // у ивента появится здесь сам, как только по нему пройдёт хотя бы одна игра.
  const eventCities = useMemo<Branch[]>(() => {
    const ids = new Set<ID>();
    for (const d of deals) if (d.kind === 'event' && d.mirror_branch_id != null) ids.add(d.mirror_branch_id);
    return Array.from(ids)
      .map((id) => branches.find((b) => b.id === id))
      .filter((b): b is Branch => !!b)
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [deals, branches]);

  const [eventCityId, setEventCityId] = useState<ID | 'all'>('all');
  // Выбор города имеет смысл только на ивент-филиале — на остальных сбрасываем в «Все»,
  // чтобы скрытый выбор не сужал данные обычных филиалов.
  useEffect(() => { if (!isEventBranchSelected && eventCityId !== 'all') setEventCityId('all'); }, [isEventBranchSelected, eventCityId]);

  const filtered = useMemo(
    () => deals.filter((d) =>
      (filterBranchId === 'all' || d.branch_id === filterBranchId) &&
      // Доп.фильтр по городу проведения — только когда выбран сам ивент-филиал и город не «Все».
      (!isEventBranchSelected || eventCityId === 'all' || d.mirror_branch_id === eventCityId)
    ),
    [deals, filterBranchId, isEventBranchSelected, eventCityId],
  );

  // ── Пересчёт в тенге ──
  const currencyRates = useStore((s) => s.currencyRates);
  const rateLookup = useMemo(() => makeRateLookup(currencyRates), [currencyRates]);
  /**
   * Сумма сделки в тенге по курсу на дату игры. Если курса нет (справочник не загрузился
   * или по валюте нет данных) — берём сумму как есть: лучше слегка неточный итог, чем
   * потерянные деньги или пустой экран.
   */
  const kztOf = (amount: number | null | undefined, d: Deal): number => {
    const raw = amount ?? 0;
    const date = d.event.start_at ?? d.event.event_date;
    // 1) в тенге (общий знаменатель)
    const kzt = d.currency === 'KZT' ? raw : (toKzt(raw, d.currency, date, rateLookup) ?? raw);
    // 2) из тенге — в валюту показа. Курса нет → показываем в тенге, деньги не теряем.
    if (viewCurrency === 'KZT') return kzt;
    const rate = rateLookup(date, viewCurrency);
    return rate && rate > 0 ? kzt / rate : kzt;
  };
  const amountKzt = (d: Deal) => kztOf(d.amount, d);
  const factKzt = (d: Deal) => kztOf(effectiveAmount(d), d);

  // Сноска «включая N сум…»: суммы по каждой не-тенговой валюте в текущей выборке.
  const foreignTotals = useMemo(() => {
    const m = new Map<Currency, number>();
    for (const d of filtered) {
      if (d.currency === viewCurrency) continue; // «чужие» валюты — те, что не совпали с валютой показа
      m.set(d.currency, (m.get(d.currency) ?? 0) + (d.amount ?? 0));
    }
    return Array.from(m.entries()).filter(([, v]) => v > 0);
  }, [filtered, viewCurrency]);

  // Демо-«текущий пользователь» (вход — Этап 1). По нему работает «показывать только где назначен».
  const meId = useMemo(() => employees.find((e) => e.full_name === DEMO_CURRENT_USER_NAME)?.id, [employees]);

  const adjTs = (d: Deal): number | null => {
    const ts = d.event.start_at ?? d.event.event_date;
    if (!ts) return null;
    const branch = branches.find((b) => b.id === d.branch_id);
    const base = settings.use_branch_tz_in_dashboards && branch ? inBranchTz(ts, branch.tz_offset_minutes, localOff) : ts;
    return base + tzShiftMs;
  };

  // ── Игры выбранного дня ──
  const dayDeals = useMemo(() => {
    const a = day, b = day + 24 * 3600_000;
    return filtered
      .filter((d) => {
        const t = adjTs(d);
        if (t == null || t < a || t >= b) return false;
        if (onlyAssigned) return meId != null && d.staff_assignments.some((s) => s.employee_id === meId);
        return true;
      })
      .sort((x, y) => (x.event.start_at ?? 0) - (y.event.start_at ?? 0));
  }, [filtered, day, onlyAssigned, meId, branches, settings.use_branch_tz_in_dashboards, localOff, tzShiftMs]);

  // Соседи для листания карточки — игры дня в порядке списка.
  const dayNavIds = useMemo(() => dayDeals.map((d) => d.id), [dayDeals]);

  // KPI-блок филиала считаем за МЕСЯЦ выбранного дня, а проценты — относительно
  // того же месяца год назад (июнь 2026 ↔ июнь 2025).
  const monthDealsBetween = (yearShift: number) => {
    const dd = new Date(day);
    const from = new Date(dd.getFullYear() + yearShift, dd.getMonth(), 1).getTime();
    const to = new Date(dd.getFullYear() + yearShift, dd.getMonth() + 1, 1).getTime();
    return filtered.filter((d) => { const t = adjTs(d); return t != null && t >= from && t < to; });
  };
  const monthDeals = useMemo(() => monthDealsBetween(0), [filtered, day, branches, settings.use_branch_tz_in_dashboards, localOff, tzShiftMs]);
  const prevYearMonthDeals = useMemo(() => monthDealsBetween(-1), [filtered, day, branches, settings.use_branch_tz_in_dashboards, localOff, tzShiftMs]);

  const stat = (arr: Deal[]) => {
    const games = arr.length;
    const sum = arr.reduce((a, d) => a + amountKzt(d), 0);
    return { games, sum, avg: games ? Math.round(sum / games) : 0 };
  };
  const today = stat(monthDeals), prev = stat(prevYearMonthDeals);
  const pct = (cur: number, was: number) => (was > 0 ? Math.round(((cur - was) / was) * 100) : 0);
  const deltas = { sum: pct(today.sum, prev.sum), games: pct(today.games, prev.games), avg: pct(today.avg, prev.avg) };

  const totalStats = useMemo(() => ({
    games: filtered.length,
    // Сотрудники = штат (как в «Персонале»), с учётом выбранного филиала — а не число
    // разных ответственных на сделках (иначе расходилось: 9 на дашборде vs 22 в Персонале).
    employees: employees.filter((e) => filterBranchId === 'all' || e.branch_id === filterBranchId).length,
    sales: filtered.reduce((a, d) => a + amountKzt(d), 0),
    // Факт (касса): фактическая сумма с подстрахой суммой менеджера (effectiveAmount).
    fact: filtered.reduce((a, d) => a + factKzt(d), 0),
  }), [filtered, employees, filterBranchId, rateLookup, viewCurrency]);

  // Дата сделки по выбранному типу: по событию (дата игры) или по продаже (создание).
  const dealTs = (d: Deal): number | null => (dateType === 'sale' ? (d.created_at != null ? d.created_at + tzShiftMs : null) : adjTs(d));

  type MgrRow = {
    id: string; name: string; email?: string;
    bronProv: number; all: number; factSum: number; mgrSum: number; count: number;
    byStatus: Record<string, { sum: number; count: number }>;
  };

  // ── По менеджерам за период (бублик + таблица) ──
  const mgrData = useMemo(() => {
    const { from, to } = period;
    const inRange = filtered.filter((d) => { const t = dealTs(d); return t != null && t >= from && t < to; });
    const byMgr = new Map<string, MgrRow>();
    for (const d of inRange) {
      // Стадия воронки из карточки (перенос/бартер/уточнение живут только тут);
      // по ней же считаем предбронь/бронь/проведено — см. bookingOfFunnel.
      const fs = d.funnel_status ?? DEAL_TO_FUNNEL[d.status];
      const bk = bookingOfFunnel(fs);
      // Без галки «показать все статусы» считаем только бронь-стадии (предбронь/бронь/проведено),
      // отказы/уточнение исключаем. С галкой — учитываем все.
      if (!showAllStatuses && !bk) continue;
      const emp = employees.find((e) => e.id === d.responsible_id);
      const id = d.responsible_id ?? 'none';
      const name = emp?.full_name ?? 'Без менеджера';
      const row = byMgr.get(id) ?? { id, name, email: emp?.email, bronProv: 0, all: 0, factSum: 0, mgrSum: 0, count: 0, byStatus: {} };
      // Приоритет факта (договорённость с заказчиком): касса вписала фактическую — считаем по ней,
      // не вписала — по сумме менеджера. effectiveAmount = actual_amount ?? amount. Так весь отчёт
      // по менеджерам (бублик bronProv, all, колонки byStatus) идёт по реальным деньгам.
      const amt = factKzt(d);
      // Сумма менеджера (плановая, amount) по тем же играм — для сравнения с фактической и
      // выявления расхождений (просьба Аяужан 19.08). Отдельный столбец в таблице ниже.
      if (bk) { row.all += amt; row.factSum += amt; row.mgrSum += amountKzt(d); if (bk === 'bron' || bk === 'provedeno') row.bronProv += amt; }
      row.count += 1;
      // Разбивку по колонкам справа ведём по этой же воронке.
      const st = row.byStatus[fs] ?? { sum: 0, count: 0 };
      st.sum += amt; st.count += 1; row.byStatus[fs] = st;
      byMgr.set(id, row);
    }
    const rows = Array.from(byMgr.values());
    rows.sort((x, y) => (sortDesc ? y.bronProv - x.bronProv : x.bronProv - y.bronProv));
    return { rows };
  }, [filtered, period.from, period.to, dateType, showAllStatuses, sortDesc, employees, branches, settings.use_branch_tz_in_dashboards, localOff, tzShiftMs, rateLookup, viewCurrency]);

  // ── «Без менеджера» ──
  // ЕДИНЫЙ критерий с таблицей-отчётом по менеджерам (см. mgrData выше): сделка «без
  // менеджера», если её ответственный НЕ найден среди сотрудников — то есть responsible_id
  // пуст ИЛИ указывает на служебный/удалённый аккаунт, которого нет в штате. Тот же
  // employees.find(e => e.id === d.responsible_id) === undefined, что и в mgrData
  // (там такие сделки получают имя «Без менеджера»). Считаем за ТОТ ЖЕ период и с теми же
  // фильтрами филиала/города, что и весь дашборд (filtered + period + dealTs), и по той же
  // логике «показать все статусы» — чтобы цифра плитки совпадала со строкой отчёта.
  const noMgrDeals = useMemo(() => {
    const { from, to } = period;
    const empIds = new Set(employees.map((e) => e.id));
    return filtered
      .filter((d) => {
        const t = dealTs(d);
        if (t == null || t < from || t >= to) return false;
        if (d.responsible_id != null && empIds.has(d.responsible_id)) return false; // менеджер в штате есть
        const fs = d.funnel_status ?? DEAL_TO_FUNNEL[d.status];
        if (!showAllStatuses && !bookingOfFunnel(fs)) return false;
        return true;
      })
      .sort((a, b) => (a.event.start_at ?? a.event.event_date ?? 0) - (b.event.start_at ?? b.event.event_date ?? 0));
  }, [filtered, period.from, period.to, dateType, showAllStatuses, employees, branches, settings.use_branch_tz_in_dashboards, localOff, tzShiftMs]);
  const noMgrSum = useMemo(() => noMgrDeals.reduce((a, d) => a + factKzt(d), 0), [noMgrDeals, rateLookup, viewCurrency]);
  const empIdSet = useMemo(() => new Set(employees.map((e) => e.id)), [employees]);

  // Сделки одного менеджера за период. Группировка ровно как в mgrData —
  // (responsible_id ?? 'none') === rowId — плюс тот же фильтр period/статусов. Значит список
  // и его сумма совпадают со строкой отчёта (для сверки «сумма не сходится»).
  const dealsForRow = (rowId: string) => {
    const { from, to } = period;
    return filtered
      .filter((d) => {
        const t = dealTs(d);
        if (t == null || t < from || t >= to) return false;
        if ((d.responsible_id ?? 'none') !== rowId) return false;
        const fs = d.funnel_status ?? DEAL_TO_FUNNEL[d.status];
        if (!showAllStatuses && !bookingOfFunnel(fs)) return false;
        return true;
      })
      .sort((a, b) => (a.event.start_at ?? a.event.event_date ?? 0) - (b.event.start_at ?? b.event.event_date ?? 0));
  };

  // Открыть обобщённую модалку со списком сделок (плитка / любая строка отчёта).
  const openDealsList = (title: string, isNoMgr: boolean, list: Deal[]) => {
    setListModal({ title, isNoMgr, deals: list, sum: list.reduce((a, d) => a + factKzt(d), 0), navIds: list.map((d) => d.id) });
  };
  // Клик по строке отчёта: «Без менеджера» (ответственного нет в штате) → комбинированный
  // список всех сделок без менеджера; реальный менеджер → сделки именно его группы.
  const onMgrRowClick = (r: MgrRow) => {
    const isNoMgr = !empIdSet.has(r.id);
    openDealsList(isNoMgr ? 'Без менеджера' : r.name, isNoMgr, isNoMgr ? noMgrDeals : dealsForRow(r.id));
  };
  const openListDeal = (id: ID) => { const nav = listModal?.navIds; setListModal(null); setModal({ kind: 'deal', id }, nav); };

  // Сумма/кол-во по стадии воронки для строки менеджера (склейка наших статусов).
  const stageVal = (r: MgrRow, stage: typeof STAGE_COLS[number]) =>
    stage.funnels.reduce((a, s) => ({ sum: a.sum + (r.byStatus[s]?.sum ?? 0), count: a.count + (r.byStatus[s]?.count ?? 0) }), { sum: 0, count: 0 });

  // Строки для бублика — всегда по бронь-статусам.
  const donutRows = mgrData.rows.filter((r) => r.bronProv > 0);
  const donutTotal = donutRows.reduce((a, r) => a + r.bronProv, 0);

  // Итоги по выбранным (чекбоксы) или по всем.
  const sel = selectedMgrs;
  const summRows = sel.size > 0 ? mgrData.rows.filter((r) => sel.has(r.id)) : mgrData.rows;
  const totals = {
    bronProv: summRows.reduce((a, r) => a + r.bronProv, 0),
    all: summRows.reduce((a, r) => a + r.all, 0),
    factSum: summRows.reduce((a, r) => a + r.factSum, 0),
    mgrSum: summRows.reduce((a, r) => a + r.mgrSum, 0),
    count: summRows.reduce((a, r) => a + r.count, 0),
  };
  const totalsAvg = totals.count ? Math.round(totals.all / totals.count) : 0;

  // Пагинация: режем отображаемые строки таблицы.
  const totalRows = mgrData.rows.length;
  const maxPage = Math.max(0, Math.ceil(totalRows / perPage) - 1);
  const curPage = Math.min(tablePage, maxPage);
  const pageRows = mgrData.rows.slice(curPage * perPage, curPage * perPage + perPage);
  const rangeFrom = totalRows === 0 ? 0 : curPage * perPage + 1;
  const rangeTo = Math.min(totalRows, (curPage + 1) * perPage);

  // ── Продажи по месяцам (график, последние 6) ──
  const months = useMemo(() => {
    const base = new Date(); base.setDate(1); base.setHours(0, 0, 0, 0);
    const out: { ts: number; sum: number; count: number; avg: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const m = new Date(base); m.setMonth(m.getMonth() - i);
      const a = m.getTime(); const mb = new Date(m); mb.setMonth(mb.getMonth() + 1);
      const inM = filtered.filter((d) => { const t = adjTs(d); return t != null && t >= a && t < mb.getTime(); });
      const sum = inM.reduce((s, d) => s + factKzt(d), 0);
      out.push({ ts: a, sum, count: inM.length, avg: inM.length ? Math.round(sum / inM.length) : 0 });
    }
    return out;
  }, [filtered, branches, settings.use_branch_tz_in_dashboards, localOff, tzShiftMs, rateLookup, viewCurrency]);

  const dt = new Date(day);
  const dayLabel = `${dt.getDate()} ${MONTHS_GEN[dt.getMonth()]} (${DOW[dt.getDay()]})`;

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-[1280px] mx-auto p-3 sm:p-5 space-y-4">
        {/* 1. Шапка дня */}
        <div className="flex items-center justify-center flex-wrap gap-2.5 py-1">
          <button className="icon-btn !w-9 !h-9 rounded-full bg-surface-1 border border-line shadow-soft" onClick={() => setDay(addDays(day, -1))}><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setDay(startOfDay(Date.now()))} title="Вернуться на сегодня"
            className="px-4 h-9 rounded-full bg-surface-1 border border-line shadow-soft flex items-center text-sm font-semibold hover:border-accent hover:text-accent transition-colors">{dayLabel}</button>
          <button className="icon-btn !w-9 !h-9 rounded-full bg-surface-1 border border-line shadow-soft" onClick={() => setDay(addDays(day, 1))}><ChevronRight className="w-4 h-4" /></button>
          <label className="sm:ml-3 flex items-center gap-2 text-sm text-ink-2 cursor-pointer select-none">
            <span>Показывать только где назначен</span>
            <span onClick={() => setOnlyAssigned(!onlyAssigned)} className={cn('w-10 h-5 rounded-full transition-colors relative cursor-pointer', onlyAssigned ? 'bg-accent' : 'bg-surface-3')}>
              <span className={cn('absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform', onlyAssigned ? 'translate-x-[22px]' : 'translate-x-0.5')} />
            </span>
          </label>
        </div>

        {/* 2. Информация */}
        <div className="panel p-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-ink-1">Информация</h2>
              <p className="text-sm text-ink-2 mt-1">Подробнее об играх можно посмотреть в календаре.</p>
            </div>
            <button onClick={() => setPage('calendar')} className="btn-primary justify-center uppercase text-xs tracking-wider whitespace-nowrap">Открыть календарь</button>
          </div>
        </div>

        {/* 2b. Список игр на день — свёрнут по умолчанию, раскрывается по клику на заголовок. */}
        <div className="panel overflow-hidden">
          <button
            className="w-full flex items-center justify-between gap-2 px-5 py-3.5 text-left hover:bg-surface-2 transition-colors"
            onClick={() => setGamesListOpen((v) => !v)}
          >
            <span className="font-semibold text-sm text-ink-1">Список назначенных игр на день ({dayDeals.length})</span>
            <ChevronDown className={cn('w-4 h-4 text-ink-3 transition-transform flex-shrink-0', gamesListOpen && 'rotate-180')} />
          </button>
          {gamesListOpen && (dayDeals.length === 0 ? (
            <div className="px-5 py-3.5 border-t border-line text-ink-3 text-sm">Нет игр на этот день</div>
          ) : (
          <div className="overflow-x-auto border-t border-line">
           <div className="min-w-[640px] sm:min-w-0 divide-y divide-line/60">
            {dayDeals.map((d, i) => {
              const team = teams.find((t) => t.id === d.team_id);
              const fmt = game_formats.find((f) => f.id === d.event.game_format_id);
              const bk = bookingOfFunnel(d.funnel_status ?? DEAL_TO_FUNNEL[d.status]);
              const main = (fmt?.name || d.contact_name || 'Игра') + (d.event.place === 'outside' ? ' Выезд' : '');
              const teamNo = team ? (team.order ?? teams.indexOf(team) + 1) : null;
              return (
                <button key={d.id} onClick={() => setModal({ kind: 'deal', id: d.id }, dayNavIds)}
                  className="w-full flex items-center gap-3 px-5 py-2.5 hover:bg-surface-2 transition-colors text-left">
                  <span className="text-xs text-ink-3 tabular-nums w-7 flex-shrink-0">#{i + 1}</span>
                  <span className="text-sm font-medium text-ink-1 tabular-nums w-[108px] flex-shrink-0">{hhmm(d.event.start_at != null ? d.event.start_at + tzShiftMs : undefined)}{d.event.end_at ? ` - ${hhmm(d.event.end_at + tzShiftMs)}` : ''}</span>
                  <span className="text-sm text-ink-1 flex-1 truncate flex items-center gap-2">
                    <span>{fmt?.emoji || d.event.emoji || '📋'}</span>{main}
                    {bk && <span className="text-[11px] font-medium px-2 py-0.5 rounded" style={{ backgroundColor: BOOKING[bk].bg, color: BOOKING[bk].fg }}>{BOOKING[bk].label}</span>}
                  </span>
                  <span className="flex items-center gap-1 text-ink-3 flex-shrink-0">
                    {d.event.place === 'outside' && <Car className="w-4 h-4" />}
                    <Gamepad className="w-4 h-4" />
                  </span>
                  {team ? (
                    <span className="flex items-center gap-1.5 text-xs font-medium flex-shrink-0 max-w-[200px]">
                      <span className="w-5 h-5 rounded grid place-items-center text-white text-[10px] font-bold flex-shrink-0" style={{ backgroundColor: team.color }}>{teamNo}</span>
                      <span className="truncate" style={{ color: team.color }}>{team.name}</span>
                    </span>
                  ) : <span className="text-xs text-ink-3 flex-shrink-0">Без команды</span>}
                </button>
              );
            })}
           </div>
          </div>
          ))}
        </div>

        {/* 3. KPI филиала + Сводная статистика */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="panel p-5">
            {/* На телефоне выпадашки не влезают в ряд и уезжают вбок — поэтому колонкой
                (flex-col), а с sm и шире возвращаемся в ряд. Иконку держим при филиале. */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
              <div className="flex items-center gap-2 min-w-0">
                <Building2 className="w-4 h-4 text-ink-3 flex-shrink-0" />
                {!canSwitchCity && allowedBranches.length <= 1 ? (
                  // Привязан к одному городу — без выбора, просто название филиала.
                  <span className="font-bold text-ink-1 px-2 h-8 inline-flex items-center">{allowedBranches[0]?.name ?? '—'}</span>
                ) : (
                  <select value={filterBranchId} onChange={(e) => setFilterBranch(e.target.value as ID | 'all')}
                    className="font-bold text-ink-1 bg-transparent border border-line rounded-lg px-2 h-8 cursor-pointer hover:border-accent focus:border-accent outline-none w-full sm:w-auto">
                    <option value="all">{canSwitchCity ? 'Все филиалы' : 'Мои филиалы'}</option>
                    {allowedBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                )}
              </div>
              {/* Город проведения ивента — виден ТОЛЬКО на ивент-филиале. Список динамический
                  (уникальные mirror_branch_id ивент-сделок). Сужает весь дашборд по городу.
                  На телефоне встаёт ОТДЕЛЬНОЙ строкой под филиалом (w-full), с sm — в ряд. */}
              {isEventBranchSelected && eventCities.length > 0 && (
                <select value={eventCityId} onChange={(e) => setEventCityId(e.target.value as ID | 'all')}
                  title="Город проведения ивента"
                  className="font-bold text-ink-1 bg-transparent border border-line rounded-lg px-2 h-8 cursor-pointer hover:border-accent focus:border-accent outline-none w-full sm:w-auto">
                  <option value="all">Все города</option>
                  {eventCities.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              )}
              {/* Валюта показа: все суммы дашборда пересчитываются в неё по курсу на дату игры. */}
              <select value={viewCurrency} onChange={(e) => setViewCurrency(e.target.value as Currency)}
                title="Валюта, в которой показывать суммы"
                className="w-full sm:w-auto sm:ml-auto text-xs text-ink-2 bg-transparent border border-line rounded-lg px-2 h-8 cursor-pointer hover:border-accent focus:border-accent outline-none">
                <option value="KZT">₸ тенге</option>
                <option value="UZS">сум</option>
                <option value="KGS">сом</option>
                <option value="AZN">₼ манат</option>
              </select>
            </div>
            <div className="text-xs text-ink-3 mb-3">{selectedBranch?.name ?? (canSwitchCity ? 'Все филиалы' : 'Мои филиалы')} ·{MONTHS_NOM[dt.getMonth()]} {dt.getFullYear()} <span className="text-ink-3/70">(% — к тому же месяцу {dt.getFullYear() - 1} г.)</span></div>
            <div className="grid grid-cols-3 gap-4">
              <Kpi label="Сумма" value={fmtShort(today.sum, cur)} delta={deltas.sum} />
              <Kpi label="Игры" value={String(today.games)} delta={deltas.games} />
              <Kpi label="Средний чек" value={fmtShort(today.avg, cur)} delta={deltas.avg} />
            </div>
            <button onClick={() => setPage('crm')} className="btn-primary mt-4 uppercase text-xs tracking-wider">Посмотреть игры</button>
          </div>

          <div className="panel p-5">
            <h3 className="text-lg font-bold text-ink-1">Сводная статистика</h3>
            <p className="text-xs text-ink-3 mt-1">За весь период времени</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <Stat icon={Gamepad2} label="Игры" value={String(totalStats.games)} bg="bg-warm-yellow/90" />
              <Stat icon={Users} label="Сотрудники" value={String(totalStats.employees)} bg="bg-accent/90" />
              <Stat icon={Banknote} label="Сумма продаж (план)" value={fmtShort(totalStats.sales, cur)} bg="bg-warm-mint/90" />
              {/* Факт (касса) — фактическая сумма по играм с подстрахой суммой менеджера. */}
              <Stat icon={Banknote} label="Факт (касса)" value={fmtShort(totalStats.fact, cur)} bg="bg-accent/70" />
            </div>
            {/* Сноска: часть игр продана не в тенге — показываем «сырые» суммы в их валюте,
                чтобы было видно, что цифры выше — это пересчёт, а не потеря денег. */}
            {foreignTotals.length > 0 && (
              <p className="text-[11px] text-ink-3 mt-3 leading-snug">
                включая {foreignTotals.map(([c, v]) => `${Math.round(v).toLocaleString('ru-RU')} ${CURRENCY_LABEL[c]}`).join(', ')} — пересчитано в {CURRENCY_LABEL[viewCurrency]} по курсу Нацбанка на дату игры
              </p>
            )}
          </div>
        </div>

        {/* 3b. Плитка «Без менеджера» — сделки без назначенного менеджера за выбранный период */}
        <button
          type="button"
          onClick={() => noMgrDeals.length > 0 && openDealsList('Без менеджера', true, noMgrDeals)}
          disabled={noMgrDeals.length === 0}
          className={cn(
            'panel p-5 w-full text-left flex items-center gap-4 transition-colors',
            noMgrDeals.length > 0
              ? 'border-status-refused/50 hover:border-status-refused cursor-pointer'
              : 'opacity-70 cursor-default',
          )}
        >
          <div className={cn('w-12 h-12 rounded-xl text-white grid place-items-center flex-shrink-0', noMgrDeals.length > 0 ? 'bg-status-refused/90' : 'bg-surface-3')}>
            <UserX className="w-6 h-6" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">
              Без менеджера · {periodLabel(grouping, period.from, period.to)}
            </div>
            <div className="text-2xl font-bold text-ink-1 tabular-nums leading-tight mt-0.5">
              {noMgrDeals.length} <span className="text-sm font-medium text-ink-2">игр</span>
              <span className="text-ink-3 font-normal"> · </span>
              {fmtShort(noMgrSum, cur)}
            </div>
            <div className="text-xs text-ink-3 mt-0.5">
              {noMgrDeals.length > 0 ? 'Нажмите, чтобы посмотреть список' : 'Все сделки за период с менеджером'}
            </div>
          </div>
          {noMgrDeals.length > 0 && <ChevronRight className="w-5 h-5 text-ink-3 flex-shrink-0" />}
        </button>

        {/* 4. Бублик по менеджерам + таблица */}
        <div className="panel p-5">
          <div className="flex items-center justify-between mb-2 relative">
            <div className="flex-1" />
            <div className="flex items-center gap-3">
              <button className="icon-btn !w-8 !h-8 rounded-full" onClick={() => shiftPeriod(-1)}><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm font-semibold tabular-nums">{periodLabel(grouping, period.from, period.to)}</span>
              <button className="icon-btn !w-8 !h-8 rounded-full" onClick={() => shiftPeriod(1)}><ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 flex justify-end">
              <button onClick={() => setGearOpen((o) => !o)}
                className={cn('icon-btn !w-9 !h-9 rounded-full border', gearOpen ? 'border-accent text-accent bg-accent-soft' : 'border-accent/40 text-accent')}>
                <SettingsIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Окошко настроек (шестерёнка) */}
            {gearOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setGearOpen(false)} />
                <div className="absolute right-0 top-11 z-30 w-72 panel shadow-xl p-4 space-y-3">
                  <label className="flex items-center gap-2.5 text-sm text-ink-1 cursor-pointer select-none">
                    <input type="checkbox" className="w-4 h-4 accent-accent" checked={showAllStatuses} onChange={(e) => setShowAllStatuses(e.target.checked)} />
                    Показать все статусы
                  </label>
                  <SelectField label="Тип даты" value={dateType} onChange={(v) => setDateType(v as DateType)}
                    options={[{ v: 'event', label: 'По дате события' }, { v: 'sale', label: 'По дате продажи' }]} />
                  <SelectField label="Тип группировки" value={grouping} onChange={(v) => setGrouping(v as Grouping)}
                    options={[{ v: 'month', label: 'По месяцам' }, { v: 'week', label: 'По неделям' }, { v: 'custom', label: 'Произвольные даты' }]} />
                  {grouping === 'custom' && (
                    <div className="space-y-2">
                      <DateField label="Дата начала" value={customFrom} onChange={setCustomFrom} />
                      <DateField label="Дата окончания" value={customTo} onChange={setCustomTo} />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex justify-center my-4">
            <Donut segments={donutRows.map((r, i) => ({ value: r.bronProv, color: SEG_COLORS[i % SEG_COLORS.length], label: r.name, amount: fmtSpaced(r.bronProv, cur) }))} centerValue={fmtSpaced(donutTotal, cur)} />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink-3 text-xs border-b border-line">
                  <th className="py-2 px-2 font-medium w-8" />
                  <th className="py-2 px-2 font-medium">Менеджер</th>
                  <th className="py-2 px-1 font-medium w-6 text-center">
                    <button onClick={() => setSortDesc((s) => !s)} className="hover:text-accent">{sortDesc ? '↓' : '↑'}</button>
                  </th>
                  <th className="py-2 px-2 font-medium text-right">Сумма броней и проведенных</th>
                  <th className="py-2 px-2 font-medium text-right">Сумма предбронь, броней и проведенных</th>
                  <th className="py-2 px-2 font-medium text-right whitespace-nowrap">Фактическая</th>
                  <th className="py-2 px-2 font-medium text-right whitespace-nowrap">Сумма менеджера</th>
                  <th className="py-2 px-2 font-medium text-right whitespace-nowrap">Кол-во игр</th>
                  <th className="py-2 px-2 font-medium text-right">Средний чек</th>
                  <th className="py-2 px-2 font-medium text-right">Удаленные</th>
                  {showAllStatuses && STAGE_COLS.map((c) => (
                    <Fragment key={c.key}>
                      <th className="py-2 px-2 font-medium text-right whitespace-nowrap">{c.label}</th>
                      <th className="py-2 px-1 font-medium text-center text-ink-3">*</th>
                    </Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mgrData.rows.length === 0 && (
                  <tr><td colSpan={10} className="py-6 text-center text-ink-3 italic">Нет данных за этот период</td></tr>
                )}
                {pageRows.map((r) => {
                  const checked = selectedMgrs.has(r.id);
                  // Строка «Без менеджера» — тот же критерий, что и в mgrData: ответственного нет в штате.
                  const isNoMgr = !employees.some((e) => e.id === r.id);
                  // ВСЯ строка кликабельна: открывает список сделок именно этого менеджера
                  // (для сверки, когда сумма не сходится). Чекбокс-ячейка клик не пробрасывает.
                  return (
                    <tr key={r.id} onClick={() => onMgrRowClick(r)} title="Показать сделки этого менеджера"
                      className={cn('border-b border-line/50 cursor-pointer', checked ? 'bg-accent-soft/50' : 'hover:bg-surface-2/50')}>
                      <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" className="w-4 h-4 accent-accent" checked={checked}
                          onChange={() => setSelectedMgrs((prev) => { const n = new Set(prev); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; })} />
                      </td>
                      <td className="py-2 px-2">
                        {isNoMgr ? (
                          <div className="flex items-center gap-2.5 group">
                            <span className="w-9 h-9 rounded-full grid place-items-center bg-status-refused/15 text-status-refused flex-shrink-0"><UserX className="w-4 h-4" /></span>
                            <div className="min-w-0">
                              <div className="font-medium text-status-refused truncate group-hover:underline">{r.name}</div>
                              <div className="text-[11px] text-ink-3 truncate">Показать список</div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2.5 group">
                            <span className="w-9 h-9 rounded-full grid place-items-center bg-surface-2 text-ink-3 text-xs font-semibold flex-shrink-0">{initials(r.name)}</span>
                            <div className="min-w-0">
                              <div className="font-medium text-ink-1 truncate group-hover:text-accent group-hover:underline">{r.name}</div>
                              {r.email && <div className="text-[11px] text-ink-3 truncate">{r.email}</div>}
                            </div>
                          </div>
                        )}
                      </td>
                      <td />
                      <td className="py-2 px-2 text-right font-semibold text-emerald-600 tabular-nums">+{fmtSpaced(r.bronProv, cur)}</td>
                      <td className="py-2 px-2 text-right font-semibold text-emerald-600 tabular-nums">+{fmtSpaced(r.all, cur)}</td>
                      <td className="py-2 px-2 text-right font-semibold text-accent tabular-nums">{fmtSpaced(r.factSum, cur)}</td>
                      <td className="py-2 px-2 text-right tabular-nums text-ink-2">{fmtSpaced(r.mgrSum, cur)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{r.count}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{r.count ? fmtSpaced(Math.round(r.all / r.count), cur) : `0 ${cur}`}</td>
                      <td className="py-2 px-2 text-right text-ink-3 tabular-nums">0</td>
                      {showAllStatuses && STAGE_COLS.map((c) => {
                        const v = stageVal(r, c);
                        return (
                          <Fragment key={c.key}>
                            <td className="py-2 px-2 text-right tabular-nums">{Math.round(v.sum).toLocaleString('ru-RU')}</td>
                            <td className="py-2 px-1 text-center text-ink-3 tabular-nums">{v.count}</td>
                          </Fragment>
                        );
                      })}
                    </tr>
                  );
                })}
                {mgrData.rows.length > 0 && (
                  <tr className={cn('font-semibold', sel.size > 0 ? 'bg-accent-soft text-accent' : 'bg-accent-soft/40')}>
                    <td className="py-2 px-2" />
                    <td className="py-2 px-2 text-ink-2">{sel.size > 0 ? `Выбрано (${sel.size}):` : 'Итого:'}</td>
                    <td />
                    <td className={cn('py-2 px-2 text-right tabular-nums', sel.size > 0 ? 'text-accent' : 'text-emerald-600')}>+{fmtSpaced(totals.bronProv, cur)}</td>
                    <td className={cn('py-2 px-2 text-right tabular-nums', sel.size > 0 ? 'text-accent' : 'text-emerald-600')}>+{fmtSpaced(totals.all, cur)}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-accent">{fmtSpaced(totals.factSum, cur)}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-ink-2">{fmtSpaced(totals.mgrSum, cur)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{totals.count}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtSpaced(totalsAvg, cur)}</td>
                    <td className="py-2 px-2 text-right text-ink-3 tabular-nums">0</td>
                    {showAllStatuses && STAGE_COLS.map((c) => (
                      <Fragment key={c.key}>
                        <td className="py-2 px-2 text-right tabular-nums">{Math.round(summRows.reduce((a, r) => a + stageVal(r, c).sum, 0)).toLocaleString('ru-RU')}</td>
                        <td className="py-2 px-1 text-center text-ink-3 tabular-nums">{summRows.reduce((a, r) => a + stageVal(r, c).count, 0)}</td>
                      </Fragment>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Пагинация */}
          {totalRows > 0 && (
            <div className="flex items-center justify-end gap-4 mt-3 text-xs text-ink-3">
              <div className="flex items-center gap-2">
                <span>Строк на странице:</span>
                <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setTablePage(0); }}
                  className="border border-line rounded-md px-2 py-1 bg-surface-1 text-ink-1 outline-none">
                  {[5, 10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <span className="tabular-nums">{rangeFrom}–{rangeTo} из {totalRows}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => setTablePage(0)} disabled={curPage === 0} className="icon-btn !w-7 !h-7 rounded disabled:opacity-30">«</button>
                <button onClick={() => setTablePage(curPage - 1)} disabled={curPage === 0} className="icon-btn !w-7 !h-7 rounded disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => setTablePage(curPage + 1)} disabled={curPage >= maxPage} className="icon-btn !w-7 !h-7 rounded disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
                <button onClick={() => setTablePage(maxPage)} disabled={curPage >= maxPage} className="icon-btn !w-7 !h-7 rounded disabled:opacity-30">»</button>
              </div>
            </div>
          )}
        </div>

        {/* 5. График «Продажи» */}
        <div className="panel p-5">
          <h3 className="text-lg font-bold text-ink-1">Продажи</h3>
          <p className="text-xs text-ink-3 mt-1 mb-4">Данные за последние полгода по месяцам</p>
          <SalesChart months={months} cur={cur} />
        </div>
      </div>

      {/* Обобщённый список сделок (плитка «Без менеджера» / клик по любой строке отчёта) */}
      {listModal && (
        <DealsListModal
          title={listModal.title}
          isNoMgr={listModal.isNoMgr}
          deals={listModal.deals}
          sum={listModal.sum}
          cur={cur}
          periodText={periodLabel(grouping, period.from, period.to)}
          branches={branches}
          tzShiftMs={tzShiftMs}
          onOpenDeal={openListDeal}
          onClose={() => setListModal(null)}
        />
      )}
    </div>
  );
}

// ── Обобщённая модалка со списком сделок (менеджер или «Без менеджера») ──
function DealsListModal({ title, isNoMgr, deals, sum, cur, periodText, branches, tzShiftMs, onOpenDeal, onClose }: {
  title: string; isNoMgr: boolean; deals: Deal[]; sum: number; cur: string; periodText: string;
  branches: Branch[]; tzShiftMs: number; onOpenDeal: (id: ID) => void; onClose: () => void;
}) {
  const branchName = (id?: ID | null) => branches.find((b) => b.id === id)?.name ?? '—';
  const stageOf = (d: Deal) => FUNNEL_BY_KEY.get(d.funnel_status ?? DEAL_TO_FUNNEL[d.status]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[820px] max-h-[92vh] flex flex-col panel shadow-pop animate-pop">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-line">
          <div className="flex items-center gap-3 min-w-0">
            <div className={cn('w-10 h-10 rounded-xl text-white grid place-items-center flex-shrink-0', isNoMgr ? 'bg-status-refused/90' : 'bg-accent/90')}>
              {isNoMgr ? <UserX className="w-5 h-5" /> : <Users className="w-5 h-5" />}
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-ink-1 leading-tight truncate">{title}</h3>
              <p className="text-xs text-ink-3 mt-0.5 truncate">{periodText} · {deals.length} игр · {fmtSpaced(sum, cur)}</p>
            </div>
          </div>
          <button onClick={onClose} className="icon-btn !w-9 !h-9 rounded-full flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-auto p-2 sm:p-3">
          {deals.length === 0 ? (
            <div className="py-10 text-center text-ink-3 italic">Нет таких сделок за период</div>
          ) : (
            <div className="min-w-[560px] sm:min-w-0 divide-y divide-line/60">
              {deals.map((d, i) => {
                const st = stageOf(d);
                const ts = d.event.start_at ?? d.event.event_date;
                return (
                  <button key={d.id} onClick={() => onOpenDeal(d.id)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-surface-2 transition-colors text-left rounded-lg">
                    <span className="text-xs text-ink-3 tabular-nums w-6 flex-shrink-0">{i + 1}</span>
                    <span className="text-sm text-accent font-medium tabular-nums w-[74px] flex-shrink-0 pr-2">#{d.number}</span>
                    <span className="text-sm text-ink-1 flex-1 truncate">{d.contact_name || '—'}</span>
                    <span className="text-xs text-ink-2 tabular-nums w-[74px] flex-shrink-0 text-right">{ts != null ? ddmmyy(ts + tzShiftMs) : '—'}</span>
                    <span className="text-xs text-ink-2 w-[92px] flex-shrink-0 truncate">{branchName(d.branch_id)}</span>
                    <span className="text-sm font-semibold text-ink-1 tabular-nums w-[110px] flex-shrink-0 text-right">{fmtSpaced(effectiveAmount(d), CUR(d.currency))}</span>
                    {st && (
                      <span className="text-[11px] font-medium px-2 py-0.5 rounded flex-shrink-0 w-[92px] text-center truncate" style={{ backgroundColor: st.color + '22', color: st.color }}>{st.label}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── KPI ячейка с дельтой (красная/зелёная) ──
function Kpi({ label, value, delta }: { label: string; value: string; delta: number }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">{label}</div>
      <div className="text-3xl font-bold text-accent mt-1 tabular-nums">{value}</div>
      <div className={cn('text-xs font-semibold mt-0.5', delta < 0 ? 'text-status-refused' : 'text-warm-mint')}>{delta >= 0 ? '+' : ''}{delta}%</div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, bg }: { icon: typeof Gamepad2; label: string; value: string; bg: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className={cn('w-12 h-12 rounded-xl text-white grid place-items-center flex-shrink-0', bg)}><Icon className="w-6 h-6" /></div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">{label}</div>
        <div className="text-2xl font-bold text-ink-1 tabular-nums leading-tight">{value}</div>
      </div>
    </div>
  );
}

// Затемнение hex-цвета (для выделенного нажатием сегмента).
function darken(hex: string, k = 0.62): string {
  const m = hex.replace('#', '');
  const r = Math.round(parseInt(m.slice(0, 2), 16) * k);
  const g = Math.round(parseInt(m.slice(2, 4), 16) * k);
  const b = Math.round(parseInt(m.slice(4, 6), 16) * k);
  return `rgb(${r},${g},${b})`;
}

// ── Кольцевая диаграмма (по менеджерам) ──
function Donut({ segments, centerValue }: { segments: { value: number; color: string; label: string; amount: string }[]; centerValue: string }) {
  const SIZE = 408, R = 156, STROKE = 56, C = 2 * Math.PI * R, cx = SIZE / 2, cy = SIZE / 2;
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  const [hover, setHover] = useState<number | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  let off = 0;
  const arcs = segments.filter((s) => s.value > 0).map((s) => {
    const pct = s.value / total, len = pct * C, mid = off + len / 2;
    const angle = (mid / C) * 2 * Math.PI - Math.PI / 2;
    const arc = {
      pct, len, dashoffset: -off, color: s.color, label: s.label, amount: s.amount,
      lx: cx + R * Math.cos(angle), ly: cy + R * Math.sin(angle),
      // координаты для плашки — на внешнем крае, по центру дуги
      tx: cx + (R + STROKE / 2) * Math.cos(angle), ty: cy + (R + STROKE / 2) * Math.sin(angle),
    };
    off += len; return arc;
  });
  // Активный сегмент для центра: при наведении — он; иначе зафиксированный кликом.
  const activeIdx = hover != null ? hover : selected;
  const activeArc = activeIdx != null ? arcs[activeIdx] : null;
  const tip = hover != null ? arcs[hover] : null;
  // Плашка слева/справа от бублика по тому, в какой половине сегмент.
  const tipLeftHalf = tip ? tip.tx < cx : false;

  return (
    <div className="relative w-[408px] max-w-full aspect-square" style={{ maxWidth: SIZE }}>
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-full h-full -rotate-90">
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="rgb(var(--surface-3))" strokeWidth={STROKE} />
        {arcs.map((a, i) => (
          <circle
            key={i} cx={cx} cy={cy} r={R} fill="none"
            stroke={selected === i ? darken(a.color) : a.color}
            strokeWidth={hover === i ? STROKE + 8 : STROKE}
            strokeDasharray={`${a.len} ${C - a.len}`} strokeDashoffset={a.dashoffset}
            className="cursor-pointer transition-all"
            onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
            onClick={() => setSelected((s) => (s === i ? null : i))}
          />
        ))}
      </svg>
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="absolute inset-0 pointer-events-none">
        {arcs.filter((a) => a.pct >= 0.03).map((a, i) => (
          <text key={i} x={a.lx} y={a.ly} fill="white" fontSize="13" fontWeight="600" textAnchor="middle" dominantBaseline="middle">{(a.pct * 100).toFixed(1)}%</text>
        ))}
      </svg>
      <div className="absolute inset-0 grid place-items-center pointer-events-none">
        <div className="text-center px-10">
          {activeArc ? (
            <>
              <div className="text-xl font-medium leading-tight" style={{ color: activeArc.color }}>{activeArc.label}</div>
              <div className="text-2xl font-medium text-ink-1 mt-1 tabular-nums">{activeArc.amount}</div>
            </>
          ) : (
            <>
              <div className="text-base font-normal text-ink-2">Сумма</div>
              <div className="text-2xl font-medium text-ink-1 mt-1 tabular-nums">{centerValue}</div>
            </>
          )}
        </div>
      </div>
      {tip && (
        <div
          className="absolute z-10 pointer-events-none rounded-md px-3 py-1.5 shadow-lg text-white text-xs font-semibold whitespace-nowrap"
          style={{
            backgroundColor: tip.color,
            top: `${(tip.ty / SIZE) * 100}%`,
            left: `${(tip.tx / SIZE) * 100}%`,
            transform: `translate(${tipLeftHalf ? '-100%' : '0'}, -50%)`,
          }}
        >
          {tip.label}: {tip.amount}
        </div>
      )}
    </div>
  );
}

// ── Выпадающий список (тип даты / тип группировки) ──
function SelectField({ label, value, options, onChange }: { label: string; value: string; options: { v: string; label: string }[]; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.v === value);
  return (
    <div className="relative">
      <div className="absolute -top-2 left-2.5 px-1 bg-surface-1 text-[10px] text-ink-3 z-10">{label}</div>
      <button onClick={() => setOpen((o) => !o)}
        className={cn('w-full flex items-center justify-between border rounded-md px-3 py-2.5 text-sm text-ink-1 bg-surface-1', open ? 'border-accent' : 'border-line')}>
        {current?.label}
        <ChevronDown className={cn('w-4 h-4 text-ink-3 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 top-full mt-1 z-30 panel shadow-xl py-1 overflow-hidden">
            {options.map((o) => (
              <button key={o.v} onClick={() => { onChange(o.v); setOpen(false); }}
                className={cn('w-full text-left px-3 py-2.5 text-sm hover:bg-surface-2', o.v === value ? 'bg-accent-soft text-accent font-medium' : 'text-ink-1')}>
                {o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Поле выбора даты с нарисованным календарём (как на эталоне) ──
const MONTHS_SHORT = ['Янв.', 'Фев.', 'Март', 'Апр.', 'Май', 'Июнь', 'Июль', 'Авг.', 'Сент.', 'Окт.', 'Нояб.', 'Дек.'];
const DOW_SHORT = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];

function DateField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => startOfMonth(value));   // отображаемый месяц
  const [draft, setDraft] = useState(value);                     // выбранный день (до «Выбрать»)
  const [pickMonth, setPickMonth] = useState(false);
  const today = startOfDay(new Date().getTime());

  const openCal = () => { setView(startOfMonth(value)); setDraft(value); setPickMonth(false); setOpen(true); };

  const vd = new Date(view);
  const year = vd.getFullYear(), month = vd.getMonth();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // Пн=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="relative">
      <button onClick={openCal} className="w-full flex items-center gap-2 border border-line rounded-md px-3 py-2 bg-surface-1 text-sm text-ink-1">
        <CalendarIcon className="w-4 h-4 text-ink-3 flex-shrink-0" />
        {ddmmyy(value)}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 z-40 w-64 panel shadow-xl p-3">
            <div className="text-center text-sm font-semibold text-ink-1 mb-2">{label}: {ddmmyy(draft)}</div>
            {pickMonth ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <button onClick={() => setView(new Date(year - 1, month, 1).getTime())} className="icon-btn !w-7 !h-7 rounded-full"><ChevronLeft className="w-4 h-4" /></button>
                  <span className="text-sm font-semibold text-ink-1">{year}</span>
                  <button onClick={() => setView(new Date(year + 1, month, 1).getTime())} className="icon-btn !w-7 !h-7 rounded-full"><ChevronRight className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-3 gap-1">
                  {MONTHS_SHORT.map((m, i) => (
                    <button key={m} onClick={() => { setView(new Date(year, i, 1).getTime()); setPickMonth(false); }}
                      className={cn('py-2.5 rounded-md text-sm hover:bg-surface-2', i === month ? 'bg-accent text-white font-medium' : 'text-ink-1')}>{m}</button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <button onClick={() => setView(addMonths(view, -1))} className="icon-btn !w-7 !h-7 rounded-full"><ChevronLeft className="w-4 h-4" /></button>
                  <button onClick={() => setPickMonth(true)} className="px-3 py-1 rounded-md hover:bg-surface-2 text-sm font-medium text-ink-1">{MONTHS_SHORT[month]} <span className="text-ink-3">{year}</span></button>
                  <button onClick={() => setView(addMonths(view, 1))} className="icon-btn !w-7 !h-7 rounded-full"><ChevronRight className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-7 gap-0.5 mb-1">
                  {DOW_SHORT.map((d) => <div key={d} className="text-center text-[11px] text-ink-3 py-1">{d}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-0.5">
                  {cells.map((c, i) => {
                    if (c == null) return <div key={i} />;
                    const ts = new Date(year, month, c).getTime();
                    const isSel = ts === startOfDay(draft);
                    const isToday = ts === today;
                    return (
                      <button key={i} onClick={() => setDraft(ts)}
                        className={cn('h-8 rounded-md text-sm grid place-items-center hover:bg-surface-2',
                          isSel ? 'bg-accent text-white font-semibold' : isToday ? 'border border-accent text-accent' : 'text-ink-1')}>{c}</button>
                    );
                  })}
                </div>
              </>
            )}
            <div className="flex gap-2 mt-3">
              <button onClick={() => setOpen(false)} className="flex-1 py-2 rounded-md bg-surface-2 text-ink-2 text-sm font-medium uppercase">Закрыть</button>
              <button onClick={() => { onChange(startOfDay(draft)); setOpen(false); }} className="flex-1 py-2 rounded-md bg-accent text-white text-sm font-medium uppercase">Выбрать</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Округление вверх до «красивого» числа (1/2/2.5/5/10 × степень 10) — для верхней границы оси.
function niceCeil(v: number): number {
  if (v <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / p;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return step * p;
}

// ── Линейный график продаж (3 серии, 3 свои оси: Сумма / Количество / Средний чек) ──
function SalesChart({ months, cur }: { months: { ts: number; sum: number; count: number; avg: number }[]; cur: string }) {
  const W = 1120, H = 380, padL = 86, padR = 212, padT = 42, padB = 52;
  const iW = W - padL - padR, iH = H - padT - padB;
  const plotR = padL + iW;
  const n = months.length;
  const xAt = (i: number) => padL + (n <= 1 ? iW / 2 : (i / (n - 1)) * iW);

  // Верхнюю границу каждой оси берём с запасом (×1.2), чтобы линии не упирались в потолок.
  const series = [
    { key: 'sum', label: 'Сумма', axisTitle: 'Сумма, ₸', color: '#7F77DD', vals: months.map((m) => m.sum), fmt: (v: number) => fmtShort(v, cur), tick: (v: number) => fmtShort(v, cur) },
    { key: 'count', label: 'Количество', axisTitle: 'Количество', color: '#7CB518', vals: months.map((m) => m.count), fmt: (v: number) => String(v), tick: (v: number) => `${Math.round(v)} игр` },
    { key: 'avg', label: 'Средний чек', axisTitle: 'Средний чек, ₸', color: '#9AA0A6', vals: months.map((m) => m.avg), fmt: (v: number) => fmtSpaced(v, cur), tick: (v: number) => `${Math.round(v).toLocaleString('ru-RU')} ${cur}` },
  ].map((s) => ({ ...s, axisMax: niceCeil(Math.max(1, ...s.vals) * 1.2) }));

  const yAt = (axisMax: number, v: number) => padT + iH - (v / axisMax) * iH;
  const FRACS = [0, 0.25, 0.5, 0.75, 1];
  const smooth = (pts: [number, number][]) => {
    if (pts.length < 2) return pts.length ? `M ${pts[0][0]} ${pts[0][1]}` : '';
    let d = `M ${pts[0][0]} ${pts[0][1]}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const [x0, y0] = pts[i], [x1, y1] = pts[i + 1], cxm = (x0 + x1) / 2;
      d += ` C ${cxm} ${y0}, ${cxm} ${y1}, ${x1} ${y1}`;
    }
    return d;
  };

  const [hover, setHover] = useState<number | null>(null);
  const bandW = n > 1 ? iW / (n - 1) : iW;
  const hm = hover != null ? months[hover] : null;
  const hx = hover != null ? xAt(hover) : 0;
  const tipLeft = hover != null && hover > (n - 1) / 2;   // во второй половине — плашку слева

  // Оси: Сумма — слева; Количество — сразу справа от поля; Средний чек — крайняя правая.
  const sumAxis = series[0], countAxis = series[1], avgAxis = series[2];

  return (
    <div className="overflow-x-auto">
      <div className="relative min-w-[860px]">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 'auto' }}>
          {/* крупная сетка: горизонтали по делениям + вертикали по месяцам */}
          {FRACS.map((g) => (
            <line key={`h${g}`} x1={padL} x2={plotR} y1={padT + iH - g * iH} y2={padT + iH - g * iH} stroke="rgb(var(--line))" strokeWidth="1.25" />
          ))}
          {months.map((_, i) => (
            <line key={`v${i}`} x1={xAt(i)} x2={xAt(i)} y1={padT} y2={padT + iH} stroke="rgb(var(--line))" strokeWidth="1" opacity="0.7" />
          ))}

          {/* подписи оси «Сумма» (слева) */}
          {FRACS.map((g) => (
            <text key={`ls${g}`} x={padL - 10} y={padT + iH - g * iH + 3} fill={sumAxis.color} fontSize="11" fontWeight="600" textAnchor="end">{sumAxis.tick(g * sumAxis.axisMax)}</text>
          ))}
          <text x={20} y={padT + iH / 2} fill={sumAxis.color} fontSize="12" fontWeight="600" textAnchor="middle" transform={`rotate(-90 20 ${padT + iH / 2})`}>{sumAxis.axisTitle}</text>

          {/* подписи оси «Количество» (внутренняя правая) */}
          {FRACS.map((g) => (
            <text key={`lc${g}`} x={plotR + 12} y={padT + iH - g * iH + 3} fill={countAxis.color} fontSize="11" fontWeight="600" textAnchor="start">{countAxis.tick(g * countAxis.axisMax)}</text>
          ))}
          <text x={plotR + 84} y={padT + iH / 2} fill={countAxis.color} fontSize="12" fontWeight="600" textAnchor="middle" transform={`rotate(-90 ${plotR + 84} ${padT + iH / 2})`}>{countAxis.axisTitle}</text>

          {/* подписи оси «Средний чек» (крайняя правая) */}
          {FRACS.map((g) => (
            <text key={`la${g}`} x={plotR + 100} y={padT + iH - g * iH + 3} fill={avgAxis.color} fontSize="11" fontWeight="600" textAnchor="start">{avgAxis.tick(g * avgAxis.axisMax)}</text>
          ))}
          <text x={W - 8} y={padT + iH / 2} fill={avgAxis.color} fontSize="12" fontWeight="600" textAnchor="middle" transform={`rotate(-90 ${W - 8} ${padT + iH / 2})`}>{avgAxis.axisTitle}</text>

          {/* вертикальный срез при наведении */}
          {hover != null && (
            <line x1={hx} x2={hx} y1={padT} y2={padT + iH} stroke="rgb(var(--ink-3))" strokeWidth="1" strokeDasharray="4 4" />
          )}
          {/* линии */}
          {series.map((s) => {
            const pts = s.vals.map((v, i) => [xAt(i), yAt(s.axisMax, v)] as [number, number]);
            return <path key={s.key} d={smooth(pts)} fill="none" stroke={s.color} strokeWidth="3" strokeLinecap="round" />;
          })}
          {/* подписи значений */}
          {series.map((s) => s.vals.map((v, i) => {
            const x = xAt(i), y = yAt(s.axisMax, v), txt = s.fmt(v);
            return (
              <g key={`${s.key}-${i}`}>
                <rect x={x - txt.length * 3.4 - 5} y={y - 19} width={txt.length * 6.8 + 10} height="16" rx="4" fill={s.color} opacity="0.92" />
                <text x={x} y={y - 11} fill="white" fontSize="10" fontWeight="600" textAnchor="middle">{txt}</text>
                <circle cx={x} cy={y} r={hover === i ? 5 : 3.5} fill={hover === i ? s.color : 'white'} stroke={s.color} strokeWidth="2" />
              </g>
            );
          }))}
          {/* ось X — месяцы */}
          {months.map((m, i) => {
            const d = new Date(m.ts);
            return <text key={i} x={xAt(i)} y={H - 24} fill="rgb(var(--ink-3))" fontSize="11" textAnchor="middle">Месяц {d.getFullYear()}-{String(d.getMonth() + 1).padStart(2, '0')}</text>;
          })}
          {/* зоны наведения по месяцам */}
          {months.map((_, i) => (
            <rect key={i} x={xAt(i) - bandW / 2} y={padT} width={bandW} height={iH} fill="transparent"
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
          ))}
        </svg>
        {/* плашка-срез при наведении */}
        {hm && (
          <div className="absolute z-10 pointer-events-none panel shadow-xl px-3 py-2 text-xs"
            style={{ left: `${(hx / W) * 100}%`, top: '34%', transform: `translate(${tipLeft ? 'calc(-100% - 12px)' : '12px'}, 0)` }}>
            <div className="font-semibold text-ink-1 mb-1.5">Месяц {new Date(hm.ts).getFullYear()}-{String(new Date(hm.ts).getMonth() + 1).padStart(2, '0')}</div>
            {series.map((s) => (
              <div key={s.key} className="flex items-center gap-2 py-0.5">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                <span className="text-ink-2">{s.label}:</span>
                <span className="font-semibold text-ink-1 tabular-nums ml-auto pl-3">{s.fmt(s.vals[hover!])}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* легенда */}
      <div className="flex items-center justify-center gap-5 mt-2">
        {series.map((s) => (
          <div key={s.key} className="flex items-center gap-2 text-xs text-ink-2">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />{s.label}
          </div>
        ))}
      </div>
    </div>
  );
}
